import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { studentApi, classApi } from '../lib/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, Save, Loader2, User, Users, BookOpen, MapPin, CalendarIcon, ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const studentSchema = z.object({
  first_name: z.string().min(2, 'Prénom requis (min. 2 caractères)'),
  last_name: z.string().min(2, 'Nom requis (min. 2 caractères)'),
  birth_date: z.string().min(1, 'Date de naissance requise'),
  birth_place: z.string().optional(),
  gender: z.enum(['male', 'female'], { required_error: 'Genre requis' }),
  student_type: z.enum(['quranic', 'classical', 'mixed'], { required_error: 'Type requis' }),
  boarding_type: z.enum(['externe', 'interne', 'demi_pensionnaire']).optional(),
  address: z.string().optional(),
  class_id: z.string().optional(),
  quranic_level: z.string().optional(),
  blood_type: z.string().optional(),
  diseases: z.string().optional(),
  medical_notes: z.string().optional(),
  allergies: z.string().optional(),
  emergency_contact: z.string().optional(),
  entry_date: z.string().optional(),  // Date d'entrée à l'école
  parent_info: z.object({
    father_name: z.string().optional(),
    father_phone: z.string().optional(),
    father_profession: z.string().optional(),
    father_address: z.string().optional(),
    father_country: z.string().optional(),
    mother_name: z.string().optional(),
    mother_phone: z.string().optional(),
    mother_profession: z.string().optional(),
    mother_address: z.string().optional(),
    mother_country: z.string().optional(),
    guardian_name: z.string().optional(),
    guardian_phone: z.string().optional(),
    guardian_relation: z.string().optional(),
    guardian_address: z.string().optional(),
    guardian_country: z.string().optional(),
  }).optional(),
});

// Custom DatePicker component with year/month selectors
function BirthDatePicker({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) return new Date(value);
    return new Date(new Date().getFullYear() - 10, 0, 1); // Default to 10 years ago
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i); // Last 30 years
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  
  const handleYearChange = (year) => {
    setViewDate(new Date(parseInt(year), viewDate.getMonth(), 1));
  };

  const handleMonthChange = (month) => {
    setViewDate(new Date(viewDate.getFullYear(), parseInt(month), 1));
  };

  const handleDayClick = (day) => {
    const selectedDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    onChange(format(selectedDate, 'yyyy-MM-dd'));
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const daysInMonth = getDaysInMonth(viewDate.getFullYear(), viewDate.getMonth());
  const firstDayOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Monday = 0

  const selectedDate = value ? new Date(value) : null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal field-focus",
            !value && "text-muted-foreground"
          )}
          data-testid="input-birthdate"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(new Date(value), 'dd MMMM yyyy', { locale: fr }) : "Sélectionner une date"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 space-y-3">
          {/* Year and Month Selectors */}
          <div className="flex items-center gap-2">
            <Select value={viewDate.getMonth().toString()} onValueChange={handleMonthChange}>
              <SelectTrigger className="flex-1 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((month, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={viewDate.getFullYear().toString()} onValueChange={handleYearChange}>
              <SelectTrigger className="w-24 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="icon" className="h-7 w-7" onClick={handlePrevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium">
              {months[viewDate.getMonth()]} {viewDate.getFullYear()}
            </span>
            <Button variant="outline" size="icon" className="h-7 w-7" onClick={handleNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day headers */}
            {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map((day) => (
              <div key={day} className="h-8 w-8 flex items-center justify-center text-xs text-muted-foreground font-medium">
                {day}
              </div>
            ))}
            {/* Empty cells for alignment */}
            {Array.from({ length: adjustedFirstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="h-8 w-8" />
            ))}
            {/* Day buttons */}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const isSelected = selectedDate &&
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === viewDate.getMonth() &&
                selectedDate.getFullYear() === viewDate.getFullYear();
              const isToday =
                new Date().getDate() === day &&
                new Date().getMonth() === viewDate.getMonth() &&
                new Date().getFullYear() === viewDate.getFullYear();

              return (
                <Button
                  key={day}
                  variant={isSelected ? "default" : "ghost"}
                  size="icon"
                  className={cn(
                    "h-8 w-8 p-0 font-normal",
                    isToday && !isSelected && "bg-accent text-accent-foreground",
                    isSelected && "bg-primary text-primary-foreground"
                  )}
                  onClick={() => handleDayClick(day)}
                >
                  {day}
                </Button>
              );
            })}
          </div>

          {/* Quick select buttons */}
          <div className="flex gap-2 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => {
                const date = new Date(currentYear - 5, 0, 1);
                setViewDate(date);
              }}
            >
              5 ans
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => {
                const date = new Date(currentYear - 10, 0, 1);
                setViewDate(date);
              }}
            >
              10 ans
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => {
                const date = new Date(currentYear - 15, 0, 1);
                setViewDate(date);
              }}
            >
              15 ans
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function StudentFormPage() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [classes, setClasses] = useState([]);
  const navigate = useNavigate();

  // Load classes for dropdown
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await classApi.getAll({});
        setClasses(response.data || []);
      } catch (error) {
        console.error('Error loading classes:', error);
      }
    };
    fetchClasses();
  }, []);

  // Filtrer les classes selon le type d'élève
  const getFilteredClasses = () => {
    if (studentType === 'quranic') {
      // Classes coraniques: celles avec education_type 'coranique' OU sans level_id (par défaut coranique)
      // OU avec noms coraniques traditionnels
      return classes.filter(c => 
        c.education_type === 'coranique' ||
        c.education_type === 'quranic' ||
        !c.level_id || 
        c.level_id === '' || 
        c.name.includes('Mouhamadou') || 
        c.name.includes('Sokhna') ||
        c.name.includes('شيخ') || 
        c.name.includes('بنات')
      );
    } else if (studentType === 'classical') {
      // Classes classiques: celles avec education_type 'classique' OU avec un level_id défini
      // OU toutes les classes si aucune classe spécifiquement classique n'existe
      const classicalClasses = classes.filter(c => 
        c.education_type === 'classique' ||
        c.education_type === 'classical' ||
        c.education_type === 'franco-arabe' ||
        (c.level_id && c.level_id !== '')
      );
      // Si aucune classe classique trouvée, afficher toutes les classes
      return classicalClasses.length > 0 ? classicalClasses : classes;
    }
    // Mixte: toutes les classes
    return classes;
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      birth_date: '',
      birth_place: '',
      gender: 'male',
      student_type: 'quranic',
      boarding_type: 'externe',
      address: '',
      class_id: '',
      quranic_level: '',
      blood_type: '',
      diseases: '',
      medical_notes: '',
      allergies: '',
      entry_date: new Date().toISOString().split('T')[0],  // Date d'entrée par défaut = aujourd'hui
      emergency_contact: '',
      parent_info: {
        father_name: '',
        father_phone: '',
        father_profession: '',
        father_address: '',
        father_country: '',
        mother_name: '',
        mother_phone: '',
        mother_profession: '',
        mother_address: '',
        mother_country: '',
        guardian_name: '',
        guardian_phone: '',
        guardian_relation: '',
        guardian_address: '',
        guardian_country: '',
      },
    },
  });

  // Load student data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchStudent = async () => {
        try {
          const response = await studentApi.getById(id);
          const student = response.data;
          
          // Reset form with student data
          reset({
            first_name: student.first_name || '',
            last_name: student.last_name || '',
            birth_date: student.birth_date || '',
            birth_place: student.birth_place || '',
            gender: student.gender || 'male',
            student_type: student.student_type || 'quranic',
            boarding_type: student.boarding_type || 'externe',
            address: student.address || '',
            class_id: student.class_id || '',
            quranic_level: student.quranic_level || '',
            blood_type: student.blood_type || '',
            diseases: student.diseases || '',
            medical_notes: student.medical_notes || '',
            allergies: student.allergies || '',
            emergency_contact: student.emergency_contact || '',
            entry_date: student.entry_date || '',
            parent_info: {
              father_name: student.parent_info?.father_name || '',
              father_phone: student.parent_info?.father_phone || '',
              father_profession: student.parent_info?.father_profession || '',
              father_address: student.parent_info?.father_address || '',
              father_country: student.parent_info?.father_country || '',
              mother_name: student.parent_info?.mother_name || '',
              mother_phone: student.parent_info?.mother_phone || '',
              mother_profession: student.parent_info?.mother_profession || '',
              mother_address: student.parent_info?.mother_address || '',
              mother_country: student.parent_info?.mother_country || '',
              guardian_name: student.parent_info?.guardian_name || '',
              guardian_phone: student.parent_info?.guardian_phone || '',
              guardian_relation: student.parent_info?.guardian_relation || '',
              guardian_address: student.parent_info?.guardian_address || '',
              guardian_country: student.parent_info?.guardian_country || '',
            },
          });
        } catch (error) {
          toast.error('Erreur lors du chargement des données');
          navigate('/students');
        } finally {
          setLoadingData(false);
        }
      };
      fetchStudent();
    }
  }, [id, isEditMode, reset, navigate]);

  const studentType = watch('student_type');
  const gender = watch('gender');
  const boardingType = watch('boarding_type');

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEditMode) {
        await studentApi.update(id, data);
        toast.success('Élève modifié avec succès !');
      } else {
        await studentApi.create(data);
        toast.success('Élève inscrit avec succès !');
      }
      navigate('/students');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isEditMode ? 'Erreur lors de la modification' : 'Erreur lors de l\'inscription'));
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="student-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/students')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">
            {isEditMode ? 'Modifier l\'élève' : 'Nouvelle inscription'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {isEditMode ? 'Mettre à jour les informations' : 'Enregistrer un nouvel élève'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Tabs defaultValue="identity" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            <TabsTrigger value="identity" className="gap-2">
              <User className="w-4 h-4 hidden sm:inline" />
              Identité
            </TabsTrigger>
            <TabsTrigger value="education" className="gap-2">
              <BookOpen className="w-4 h-4 hidden sm:inline" />
              Scolarité
            </TabsTrigger>
            <TabsTrigger value="parents" className="gap-2">
              <Users className="w-4 h-4 hidden sm:inline" />
              Parents
            </TabsTrigger>
          </TabsList>

          {/* Identity Tab */}
          <TabsContent value="identity">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Informations personnelles</CardTitle>
                <CardDescription>Identité et coordonnées de l'élève</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Prénom *</Label>
                    <Input
                      id="first_name"
                      placeholder="Moussa"
                      {...register('first_name')}
                      className="field-focus"
                      data-testid="input-firstname"
                    />
                    {errors.first_name && (
                      <p className="text-sm text-destructive">{errors.first_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="last_name">Nom *</Label>
                    <Input
                      id="last_name"
                      placeholder="Diop"
                      {...register('last_name')}
                      className="field-focus"
                      data-testid="input-lastname"
                    />
                    {errors.last_name && (
                      <p className="text-sm text-destructive">{errors.last_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Date de naissance *</Label>
                    <Controller
                      name="birth_date"
                      control={control}
                      render={({ field }) => (
                        <BirthDatePicker
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    {errors.birth_date && (
                      <p className="text-sm text-destructive">{errors.birth_date.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="birth_place">Lieu de naissance</Label>
                    <Input
                      id="birth_place"
                      placeholder="Dakar"
                      {...register('birth_place')}
                      className="field-focus"
                      data-testid="input-birthplace"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="entry_date">Date d'entrée à l'école</Label>
                    <Input
                      id="entry_date"
                      type="date"
                      {...register('entry_date')}
                      className="field-focus"
                      data-testid="input-entry-date"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Genre *</Label>
                  <RadioGroup
                    value={gender}
                    onValueChange={(value) => setValue('gender', value)}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="male" id="male" data-testid="gender-male" />
                      <Label htmlFor="male" className="font-normal cursor-pointer">Garçon</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="female" id="female" data-testid="gender-female" />
                      <Label htmlFor="female" className="font-normal cursor-pointer">Fille</Label>
                    </div>
                  </RadioGroup>
                  {errors.gender && (
                    <p className="text-sm text-destructive">{errors.gender.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Textarea
                    id="address"
                    placeholder="Quartier, rue, ville..."
                    {...register('address')}
                    className="field-focus resize-none"
                    rows={3}
                    data-testid="input-address"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Education Tab */}
          <TabsContent value="education">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Informations scolaires</CardTitle>
                <CardDescription>Type d'enseignement et niveau</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label>Type d'enseignement *</Label>
                  <RadioGroup
                    value={studentType}
                    onValueChange={(value) => setValue('student_type', value)}
                    className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  >
                    <Label
                      htmlFor="quranic"
                      className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-muted/50 transition-colors [&:has(:checked)]:border-primary [&:has(:checked)]:bg-primary/5"
                    >
                      <RadioGroupItem value="quranic" id="quranic" data-testid="type-franco" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-primary" />
                          <span className="font-medium">Coranique</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Enseignement coranique
                        </p>
                      </div>
                    </Label>
                    <Label
                      htmlFor="classical"
                      className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-muted/50 transition-colors [&:has(:checked)]:border-secondary [&:has(:checked)]:bg-secondary/5"
                    >
                      <RadioGroupItem value="classical" id="classical" data-testid="type-classical" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-secondary" />
                          <span className="font-medium">Classique</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Programme académique standard
                        </p>
                      </div>
                    </Label>
                    <Label
                      htmlFor="mixed"
                      className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-muted/50 transition-colors [&:has(:checked)]:border-accent [&:has(:checked)]:bg-accent/5"
                    >
                      <RadioGroupItem value="mixed" id="mixed" data-testid="type-mixed" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-accent" />
                          <span className="font-medium">Mixte</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Coranique + Classique
                        </p>
                      </div>
                    </Label>
                  </RadioGroup>
                </div>

                <div className="space-y-3">
                  <Label>Type d'hébergement</Label>
                  <RadioGroup
                    value={boardingType}
                    onValueChange={(value) => setValue('boarding_type', value)}
                    className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  >
                    <Label
                      htmlFor="externe"
                      className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-muted/50 transition-colors [&:has(:checked)]:border-muted-foreground [&:has(:checked)]:bg-muted/30"
                    >
                      <RadioGroupItem value="externe" id="externe" data-testid="boarding-externe" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">Externe</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Ne dort pas à l'école
                        </p>
                      </div>
                    </Label>
                    <Label
                      htmlFor="demi_pensionnaire"
                      className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-muted/50 transition-colors [&:has(:checked)]:border-amber-500 [&:has(:checked)]:bg-amber-50"
                    >
                      <RadioGroupItem value="demi_pensionnaire" id="demi_pensionnaire" data-testid="boarding-demi" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-amber-600" />
                          <span className="font-medium">Demi-pensionnaire</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Déjeuner à l'école
                        </p>
                      </div>
                    </Label>
                    <Label
                      htmlFor="interne"
                      className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-muted/50 transition-colors [&:has(:checked)]:border-emerald-500 [&:has(:checked)]:bg-emerald-50"
                    >
                      <RadioGroupItem value="interne" id="interne" data-testid="boarding-interne" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Home className="w-4 h-4 text-emerald-600" />
                          <span className="font-medium">Interne</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Réside à l'école
                        </p>
                      </div>
                    </Label>
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="class_id">Classe</Label>
                    <Select 
                      value={watch('class_id') || 'none'} 
                      onValueChange={(value) => setValue('class_id', value === 'none' ? '' : value)}
                    >
                      <SelectTrigger className="field-focus" data-testid="select-class">
                        <SelectValue placeholder="Sélectionner une classe" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune classe</SelectItem>
                        {getFilteredClasses().map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>
                            {cls.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="blood_type">Groupe sanguin</Label>
                    <Select 
                      value={watch('blood_type') || 'none'} 
                      onValueChange={(value) => setValue('blood_type', value === 'none' ? '' : value)}
                    >
                      <SelectTrigger className="field-focus" data-testid="select-blood-type">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non renseigné</SelectItem>
                        <SelectItem value="A+">A+</SelectItem>
                        <SelectItem value="A-">A-</SelectItem>
                        <SelectItem value="B+">B+</SelectItem>
                        <SelectItem value="B-">B-</SelectItem>
                        <SelectItem value="AB+">AB+</SelectItem>
                        <SelectItem value="AB-">AB-</SelectItem>
                        <SelectItem value="O+">O+</SelectItem>
                        <SelectItem value="O-">O-</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="diseases">Maladies connues</Label>
                  <Textarea
                    id="diseases"
                    placeholder="Diabète, asthme, épilepsie..."
                    {...register('diseases')}
                    className="field-focus resize-none"
                    rows={2}
                    data-testid="input-diseases"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parents Tab */}
          <TabsContent value="parents">
            <div className="space-y-6">
              {/* Father */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Informations du père</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="father_name">Nom complet</Label>
                      <Input
                        id="father_name"
                        placeholder="Mamadou Diop"
                        {...register('parent_info.father_name')}
                        className="field-focus"
                        data-testid="input-father-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="father_phone">Téléphone</Label>
                      <Input
                        id="father_phone"
                        placeholder="+221 77 123 45 67"
                        {...register('parent_info.father_phone')}
                        className="field-focus"
                        data-testid="input-father-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="father_profession">Profession</Label>
                      <Input
                        id="father_profession"
                        placeholder="Commerçant"
                        {...register('parent_info.father_profession')}
                        className="field-focus"
                        data-testid="input-father-profession"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="father_address">Adresse</Label>
                      <Input
                        id="father_address"
                        placeholder="Quartier, rue, ville"
                        {...register('parent_info.father_address')}
                        className="field-focus"
                        data-testid="input-father-address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="father_country">Pays</Label>
                      <Input
                        id="father_country"
                        placeholder="Sénégal"
                        {...register('parent_info.father_country')}
                        className="field-focus"
                        data-testid="input-father-country"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Mother */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Informations de la mère</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mother_name">Nom complet</Label>
                      <Input
                        id="mother_name"
                        placeholder="Fatou Ndiaye"
                        {...register('parent_info.mother_name')}
                        className="field-focus"
                        data-testid="input-mother-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mother_phone">Téléphone</Label>
                      <Input
                        id="mother_phone"
                        placeholder="+221 77 987 65 43"
                        {...register('parent_info.mother_phone')}
                        className="field-focus"
                        data-testid="input-mother-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mother_profession">Profession</Label>
                      <Input
                        id="mother_profession"
                        placeholder="Enseignante"
                        {...register('parent_info.mother_profession')}
                        className="field-focus"
                        data-testid="input-mother-profession"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mother_address">Adresse</Label>
                      <Input
                        id="mother_address"
                        placeholder="Quartier, rue, ville"
                        {...register('parent_info.mother_address')}
                        className="field-focus"
                        data-testid="input-mother-address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mother_country">Pays</Label>
                      <Input
                        id="mother_country"
                        placeholder="Sénégal"
                        {...register('parent_info.mother_country')}
                        className="field-focus"
                        data-testid="input-mother-country"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Guardian */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Tuteur (optionnel)</CardTitle>
                  <CardDescription>Si différent des parents</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="guardian_name">Nom complet</Label>
                      <Input
                        id="guardian_name"
                        {...register('parent_info.guardian_name')}
                        className="field-focus"
                        data-testid="input-guardian-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guardian_phone">Téléphone</Label>
                      <Input
                        id="guardian_phone"
                        {...register('parent_info.guardian_phone')}
                        className="field-focus"
                        data-testid="input-guardian-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guardian_relation">Lien de parenté</Label>
                      <Input
                        id="guardian_relation"
                        placeholder="Oncle, Tante..."
                        {...register('parent_info.guardian_relation')}
                        className="field-focus"
                        data-testid="input-guardian-relation"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="guardian_address">Adresse</Label>
                      <Input
                        id="guardian_address"
                        placeholder="Quartier, rue, ville"
                        {...register('parent_info.guardian_address')}
                        className="field-focus"
                        data-testid="input-guardian-address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guardian_country">Pays</Label>
                      <Input
                        id="guardian_country"
                        placeholder="Sénégal"
                        {...register('parent_info.guardian_country')}
                        className="field-focus"
                        data-testid="input-guardian-country"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Submit */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={() => navigate('/students')}
          >
            Annuler
          </Button>
          <Button type="submit" className="rounded-full" disabled={loading} data-testid="submit-btn">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Save className="w-4 h-4 mr-2" />
            Enregistrer
          </Button>
        </div>
      </form>
    </div>
  );
}
